import React, { useState } from "react";
import classNames from "classnames/bind";
import Fuse from "fuse.js";

import { ReactComponent as ChevronRight } from "../../icons/chevron-right.svg";
import { ReactComponent as ChevronLeft } from "../../icons/chevron-left.svg";
import { ReactComponent as Search } from "../../icons/search_small.svg";
import { Input } from "../Input";

import styles from "./MobileSelect.module.css";

const cx = classNames.bind(styles);

export type Option = {
  value: string;
  label: string;
  description?: string;
};

type Props = {
  placeholder: string;
  options: Option[];
  extraOptions?: Option[];
  searchable?: boolean;
  required?: boolean;
  disabled?: boolean;
  value: string;
  onChange: (value: string) => void;
  name: string;
};

const fuzzyFilterOptions = (options: Option[], search: string): Option[] => {
  if (!search) {
    return options;
  }

  const fuse = new Fuse(options, {
    location: 0,
    distance: 30,
    threshold: 0.2,
    maxPatternLength: 100,
    keys: ["label"],
  });

  return fuse.search(search);
};

export const MobileSelect: React.FC<Props> = (props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const searchOptions = fuzzyFilterOptions(props.options, searchValue);

  const extraOptions = props.extraOptions || [];
  const fullOptions = [...searchOptions, ...extraOptions];

  const selected = [...props.options, ...extraOptions].find(
    (item) => item.value === props.value
  );

  const handleChange = (value: string) => {
    props.onChange(value);
    setIsOpen(false);
  };

  return (
    <>
      <div className={cx("select")}>
        <Input
          required={props.required}
          name={props.name}
          label={props.placeholder}
          readOnly
          value={selected?.label}
          onFocus={() => setIsOpen(true)}
          disabled={props.disabled}
        />
        <ChevronRight height={16} width={16} className={cx("selectArrow")} />
      </div>

      {isOpen && (
        <section className={cx("options")}>
          <header
            className={cx("header", {
              header_searchable: props.searchable,
            })}
          >
            {props.searchable ? (
              <>
                <button
                  className={cx("close")}
                  onClick={() => setIsOpen(false)}
                  type="button"
                >
                  <ChevronLeft width={16} height={16} />
                </button>
                <div className={cx("inputWrapper")}>
                  <input
                    autoFocus
                    placeholder={props.placeholder}
                    className={cx("input")}
                    onChange={(event) => setSearchValue(event.target.value)}
                  />
                  <Search height={16} width={16} className={cx("inputIcon")} />
                </div>
              </>
            ) : (
              props.placeholder
            )}
          </header>

          <div className={cx("items")}>
            {fullOptions.map((option) => {
              return (
                <button
                  key={option.value}
                  className={cx("item")}
                  onClick={() => handleChange(option.value)}
                  type="button"
                >
                  <span>{option.label}</span>
                  {option.description && (
                    <span className={cx("description")}>
                      {option.description}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      )}
    </>
  );
};
